/**
 * Armada Categories — Custom element for category list interactions.
 *
 * Desktop (hover): hovering an item crossfades the media, highlights
 *   the item, dims siblings, reveals the doodle, and triggers a
 *   per-letter glitch effect on the hovered category text.
 * Mobile (touch):  static fallback media with a continuous ambient
 *   per-letter glitch effect across all items (no hover required).
 * Keyboard: focus shows the doodle (handled via CSS :focus-within).
 */
if (!customElements.get('armada-categories')) {
  class ArmadaCategories extends HTMLElement {
    constructor() {
      super();
      this._onItemEnter = this._onItemEnter.bind(this);
      this._onListLeave = this._onListLeave.bind(this);
      this._onItemTouch = this._onItemTouch.bind(this);
      this._onHoverChange = this._onHoverChange.bind(this);
      this._onMobileChange = this._onMobileChange.bind(this);
      this._glitchTimer = null;
      this._glitchingItem = null;
      this._mobileGlitchTimer = null;
      this._prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
    }

    connectedCallback() {
      this.items = this.querySelectorAll('[data-category-item]');
      this.images = this.querySelectorAll('[data-category-image]');
      this.list = this.querySelector('[data-category-list]');
      this._announcement = this.querySelector('[data-category-announcement]');
      this._hoverMql = window.matchMedia('(hover: hover)');
      this._mobileMql = window.matchMedia('(max-width: 989px)');

      this._splitAllLetters();
      this._attachAll();
      this._hoverMql.addEventListener('change', this._onHoverChange);
      this._mobileMql.addEventListener('change', this._onMobileChange);
      this._setActiveImage(0);

      if (this._mobileMql.matches) {
        this._startMobileGlitch();
      }
    }

    disconnectedCallback() {
      this._detachAll();
      this._stopGlitch();
      this._stopMobileGlitch();
      if (this._hoverMql) {
        this._hoverMql.removeEventListener('change', this._onHoverChange);
      }
      if (this._mobileMql) {
        this._mobileMql.removeEventListener('change', this._onMobileChange);
      }
    }

    _onHoverChange() {
      this._detachAll();
      this._attachAll();
    }

    _onMobileChange() {
      if (this._mobileMql.matches) {
        this._stopGlitch();
        this._startMobileGlitch();
      } else {
        this._stopMobileGlitch();
      }
    }

    _attachAll() {
      const self = this;
      this.items.forEach((item) => {
        item.addEventListener('mouseenter', self._onItemEnter);
        item.addEventListener('touchstart', self._onItemTouch, { passive: true });
      });

      if (this.list) {
        this.list.addEventListener('mouseleave', this._onListLeave);
      }
    }

    _detachAll() {
      const self = this;
      this.items.forEach((item) => {
        item.removeEventListener('mouseenter', self._onItemEnter);
        item.removeEventListener('touchstart', self._onItemTouch);
      });

      if (this.list) {
        this.list.removeEventListener('mouseleave', this._onListLeave);
      }
    }

    /* ------------------------------------------------------------------
       Letter splitting — wrap each character in a span for glitch
       ------------------------------------------------------------------ */
    _splitAllLetters() {
      this.items.forEach((item) => {
        const textEl = item.querySelector('.armada-categories__text');
        if (!textEl || textEl.dataset.glitchSplit) return;

        const raw = textEl.textContent;
        textEl.textContent = '';
        textEl.dataset.glitchSplit = 'true';

        const letters = [];
        [...raw].forEach((char) => {
          if (char === ' ') {
            const sp = document.createElement('span');
            sp.style.display = 'inline-block';
            sp.style.width = '0.3em';
            sp.textContent = '\u00A0';
            textEl.appendChild(sp);
          } else {
            const upper = char.toUpperCase();
            const span = document.createElement('span');
            span.className = 'armada-categories__glitch-letter';
            span.textContent = upper;
            span.setAttribute('data-char', upper);
            textEl.appendChild(span);
            letters.push(span);
          }
        });

        item._glitchLetters = letters;
      });
    }

    /* ------------------------------------------------------------------
       Glitch loop — randomly distort 1-2 letters while hovered
       ------------------------------------------------------------------ */
    _startGlitch(item) {
      if (this._prefersReducedMotion.matches) return;
      if (this._mobileMql && this._mobileMql.matches) return;
      if (!item._glitchLetters || item._glitchLetters.length === 0) return;

      this._stopGlitch();
      this._glitchingItem = item;

      const letters = item._glitchLetters;
      const tick = () => {
        const count = 1 + Math.floor(Math.random() * 2);
        const indices = new Set();
        while (indices.size < count && indices.size < letters.length) {
          indices.add(Math.floor(Math.random() * letters.length));
        }

        indices.forEach((i) => {
          const el = letters[i];
          const tx = (Math.random() - 0.5) * 8;
          const ty = (Math.random() - 0.5) * 4;
          const skew = (Math.random() - 0.5) * 20;
          const s1 = `${(Math.random() - 0.5) * 6}px ${(Math.random() - 0.5) * 2}px`;
          const s2 = `${(Math.random() - 0.5) * 6}px ${(Math.random() - 0.5) * 2}px`;
          const c1 = Math.random() * 60;
          const c2 = 10 + Math.random() * 30;
          const cut = (Math.random() - 0.5) * 20;

          el.classList.add('armada-categories__glitch-letter--active');
          el.style.setProperty('--gx', `${tx}px`);
          el.style.setProperty('--gy', `${ty}px`);
          el.style.setProperty('--g-skew', `${skew}deg`);
          el.style.setProperty('--g-s1', s1);
          el.style.setProperty('--g-s2', s2);
          el.style.setProperty('--g-c1', `${c1}%`);
          el.style.setProperty('--g-c2', `${c2}%`);
          el.style.setProperty('--g-cut', `${cut}px`);

          setTimeout(() => {
            el.classList.remove('armada-categories__glitch-letter--active');
            el.style.removeProperty('--gx');
            el.style.removeProperty('--gy');
            el.style.removeProperty('--g-skew');
            el.style.removeProperty('--g-s1');
            el.style.removeProperty('--g-s2');
            el.style.removeProperty('--g-c1');
            el.style.removeProperty('--g-c2');
            el.style.removeProperty('--g-cut');
          }, 60 + Math.random() * 120);
        });

        this._glitchTimer = setTimeout(tick, 100 + Math.random() * 250);
      };

      tick();
    }

    _stopGlitch() {
      clearTimeout(this._glitchTimer);
      this._glitchTimer = null;

      if (this._glitchingItem && this._glitchingItem._glitchLetters) {
        this._glitchingItem._glitchLetters.forEach((el) => {
          el.classList.remove('armada-categories__glitch-letter--active');
          el.style.removeProperty('--gx');
          el.style.removeProperty('--gy');
          el.style.removeProperty('--g-skew');
          el.style.removeProperty('--g-s1');
          el.style.removeProperty('--g-s2');
          el.style.removeProperty('--g-c1');
          el.style.removeProperty('--g-c2');
          el.style.removeProperty('--g-cut');
        });
      }

      this._glitchingItem = null;
    }

    /* ------------------------------------------------------------------
       Mobile ambient glitch — continuously distort random letters
       across all items on mobile / tablet viewport (max-width: 989px).
       ------------------------------------------------------------------ */
    _startMobileGlitch() {
      if (this._prefersReducedMotion.matches) return;

      this._stopMobileGlitch();

      const allLetters = [];
      this.items.forEach((item) => {
        if (item._glitchLetters) allLetters.push(...item._glitchLetters);
      });
      if (allLetters.length === 0) return;

      const glitchProps = ['--gx', '--gy', '--g-skew', '--g-s1', '--g-s2', '--g-c1', '--g-c2', '--g-cut'];

      const tick = () => {
        const count = 1 + Math.floor(Math.random() * 3);
        const indices = new Set();
        while (indices.size < count && indices.size < allLetters.length) {
          indices.add(Math.floor(Math.random() * allLetters.length));
        }

        indices.forEach((i) => {
          const el = allLetters[i];
          el.classList.add('armada-categories__glitch-letter--active');
          el.style.setProperty('--gx', `${(Math.random() - 0.5) * 6}px`);
          el.style.setProperty('--gy', `${(Math.random() - 0.5) * 3}px`);
          el.style.setProperty('--g-skew', `${(Math.random() - 0.5) * 16}deg`);
          el.style.setProperty('--g-s1', `${(Math.random() - 0.5) * 5}px ${(Math.random() - 0.5) * 2}px`);
          el.style.setProperty('--g-s2', `${(Math.random() - 0.5) * 5}px ${(Math.random() - 0.5) * 2}px`);
          el.style.setProperty('--g-c1', `${Math.random() * 60}%`);
          el.style.setProperty('--g-c2', `${10 + Math.random() * 30}%`);
          el.style.setProperty('--g-cut', `${(Math.random() - 0.5) * 16}px`);

          setTimeout(() => {
            el.classList.remove('armada-categories__glitch-letter--active');
            glitchProps.forEach((p) => el.style.removeProperty(p));
          }, 80 + Math.random() * 150);
        });

        this._mobileGlitchTimer = setTimeout(tick, 200 + Math.random() * 400);
      };

      tick();
    }

    _stopMobileGlitch() {
      clearTimeout(this._mobileGlitchTimer);
      this._mobileGlitchTimer = null;

      this.items.forEach((item) => {
        if (!item._glitchLetters) return;
        item._glitchLetters.forEach((el) => {
          el.classList.remove('armada-categories__glitch-letter--active');
          el.style.removeProperty('--gx');
          el.style.removeProperty('--gy');
          el.style.removeProperty('--g-skew');
          el.style.removeProperty('--g-s1');
          el.style.removeProperty('--g-s2');
          el.style.removeProperty('--g-c1');
          el.style.removeProperty('--g-c2');
          el.style.removeProperty('--g-cut');
        });
      });
    }

    /* ------------------------------------------------------------------
       Event handlers
       ------------------------------------------------------------------ */
    _onItemEnter(e) {
      const item = e.currentTarget;
      const index = parseInt(item.dataset.index, 10);

      this.setAttribute('data-hovering', '');

      this.items.forEach((i) => {
        i.classList.remove('is-active');
      });
      item.classList.add('is-active');

      this._setActiveImage(index);
      this._announce(item);
      this._startGlitch(item);
    }

    _onListLeave() {
      this.removeAttribute('data-hovering');
      this._stopGlitch();

      this.items.forEach((item) => {
        item.classList.remove('is-active');
      });

      this._setActiveImage(0);
      this._announce(this.items[0]);
    }

    _onItemTouch(e) {
      this.items.forEach((item) => {
        item.classList.remove('is-active');
      });
      e.currentTarget.classList.add('is-active');
    }

    _setActiveImage(index) {
      this.images.forEach((img) => {
        img.classList.remove('is-active');
      });

      const target = this.querySelector(`[data-category-image="${index}"]`);
      if (target) {
        target.classList.add('is-active');
      } else if (this.images[0]) {
        this.images[0].classList.add('is-active');
      }
    }

    _announce(item) {
      if (!this._announcement || !item) return;
      const text = item.querySelector('.armada-categories__text');
      if (text) {
        this._announcement.textContent = text.textContent + ' selected';
      }
    }
  }

  customElements.define('armada-categories', ArmadaCategories);
}
